import gql from 'graphql-tag'
import { apolloClient } from '@/apollo'
import axios from 'axios'
import config from '@/config'
import {
  VALIDATION_STATUS,
  SEND_CREDENTIALS_STEPS,
  DETERMINISTIC_STEPS,
  RETRY_STATUS,
  REGISTER_BY_COUNTRY,
  DETERMINISTIC_MARKETPLACE_STEPS,
  DETERMINISTIC_CAT4MRO_STEPS,
  MANUAL_VALIDATION_STEPS
} from '@/utils/validationSteps'
import { validateTaxId, validateEmail } from '@/utils/validationHelpers'

const updateSupplier = gql`
  mutation updateSupplier($id: Int!, $data: hub_supplier_register_set_input!) {
    update_hub_supplier_register_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`
const updateSupplierStep = gql`
  mutation updateSupplierStep($id: Int!, $validated_at: timestamptz!, $status: smallint!, $message: String!) {
    update_hub_supplier_step_by_pk(pk_columns: {id: $id}, _set: {validated_at: $validated_at, status: $status, message: $message}) {
      id
    }
  }
`

const insertSupplierStep = gql`
  mutation insertSupplierStep($data: hub_supplier_step_insert_input!) {
    insert_hub_supplier_step_one(object: $data) {
      id
    }
  }
`

const getOrganizationByTaxId = gql`
  query getOrganizationByTaxId($taxId: String!) {
    organization(where: {tax_id: {_eq: $taxId}}) {
      id
      name
      is_blocked_by_contract
    }
  }
`

const getCat4mroOrganizationTermsAndConditions = gql`
  query getCat4mroOrganizationTermsAndConditions($organizationId: uuid!) {
    organization_accepted_terms(where: {organization_id: {_eq: $organizationId}}) {
      id
    }
  }
`

const getUserByEmail = gql`
  query getUserByEmail($email: String!) {
    user(where: {email: {_eq: $email}}) {
      id
    }
  }
`

const getAfipSuppliersRegistered = gql`
  query getAfipSuppliersRegistered($taxId: String!) {
    hub_supplier_afip(where: {tax_id: {_eq: $taxId}}) {
      id
    }
  }
`

const getSunatSuppliersRegistered = gql`
  query getSunatSuppliersRegistered($taxId: String!) {
    hub_supplier_sunat(where: {tax_id: {_eq: $taxId}}) {
      id
    }
  }
`

const getSiiSuppliersRegistered = gql`
  query getSiiSuppliersRegistered($taxId: String!) {
    hub_supplier_sii(where: {tax_id: {_eq: $taxId}}) {
      id
    }
  }
`

async function updateStep (step, status, message) {
  try {
    await apolloClient.mutate({
      mutation: updateSupplierStep,
      variables: {
        id: Number(step.id),
        validated_at: new Date().toISOString(),
        status,
        message
      }
    })
  } catch (error) {
    console.error('Error updating supplier step:', error)
    throw error
  }
}

async function insertStep (step) {
  try {
    await apolloClient.mutate({
      mutation: insertSupplierStep,
      variables: {
        data: step
      }
    })
  } catch (error) {
    console.error('Error inserting supplier step:', error)
    throw error
  }
}

async function getMarketplaceEnterprise (taxId) {
  try {
    const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/verify`
    const { data } = await axios.get(url, { params: { taxId } })
    return data
  } catch (error) {
    const isInternalError = error.response && error.response.status === 500
    if (isInternalError) {
      console.error('Error getting marketplace enterprise:', error)
      throw error
    }
    return null
  }
}

async function getMarketplaceUser (email) {
  try {
    const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/verify`
    const { data } = await axios.get(url, { params: { email } })
    return data
  } catch (error) {
    const isInternalError = error.response && error.response.status === 500
    if (isInternalError) {
      console.error('Error getting marketplace user:', error)
      throw error
    }
    return null
  }
}

async function getCat4mroOrganization (taxId) {
  try {
    const response = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: getOrganizationByTaxId,
      variables: { taxId }
    })
    return response.data.organization[0] || null
  } catch (error) {
    console.error('Error getting cat4mro organization:', error)
    throw error
  }
}

async function getOrganizationTermsAndConditions (organizationId) {
  try {
    const response = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: getCat4mroOrganizationTermsAndConditions,
      variables: { organizationId }
    })
    return response.data.organization_accepted_terms || null
  } catch (error) {
    console.error('Error getting organization terms and conditions:', error)
    throw error
  }
}

async function getCat4mroUser (email) {
  try {
    const response = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: getUserByEmail,
      variables: { email }
    })
    return response.data.user || null
  } catch (error) {
    console.error('Error getting cat4mro user:', error)
    throw error
  }
}

async function hubspotIntegration ({supplier}) {
  try {
    if (!config.HUBSPOT.ENABLED) {
      return { status: VALIDATION_STATUS.SUCCESS, message: 'Integrado correctamente en Hubspot' }
    }

    const payload = {
      submittedAt: String(Date.now()),
      fields: [
        // Si se recibe un error similar a este:
        // Unrecognized field 'fields' (class com.hubspot.api.contact.Whatever), not marked as ignorable
        // Remover la propiedad objectTypeId de todos los objetos.
        { objectTypeId: '0-2', name: 'comprador', value: supplier.buyer_name },
        { objectTypeId: '0-2', name: 'rut', value: supplier.tax_id },
        { objectTypeId: '0-2', name: 'country', value: supplier.country },
        { objectTypeId: '0-2', name: 'tipo_de_cliente', value: supplier.customer_type },
        { objectTypeId: '0-1', name: 'nombre_empresa', value: supplier.company_name },
        { objectTypeId: '0-2', name: 'description', value: supplier.company_business },
        { objectTypeId: '0-2', name: 'address', value: supplier.company_address },
        { objectTypeId: '0-1', name: 'firstname', value: supplier.contact_name },
        { objectTypeId: '0-1', name: 'lastname', value: supplier.contact_lastname },
        { objectTypeId: '0-1', name: 'jobtitle', value: supplier.contact_position },
        { objectTypeId: '0-1', name: 'email', value: supplier.contact_email },
        { objectTypeId: '0-1', name: 'phone', value: supplier.contact_phone },
        { objectTypeId: '0-2', name: 'nombre_comercial', value: supplier.contact_legal_name },
        { objectTypeId: '0-2', name: 'apellido_comercial', value: supplier.contact_legal_lastname },
        { objectTypeId: '0-2', name: 'cargo_comercial', value: supplier.contact_legal_position },
        { objectTypeId: '0-2', name: 'notificaciones_legal', value: supplier.contact_legal_notification },
        { objectTypeId: '0-2', name: 'correo_comercial', value: supplier.contact_legal_email },
        { objectTypeId: '0-2', name: 'telefono_comercial', value: supplier.contact_legal_phone }
      ],
      context: {
        // TODO: Validar si el pageUri y el pageName seguirian siendo los mismos
        pageUri: 'registry.unilinkcorp.com',
        pageName: 'Registro Proveedores UIH'
      }
    }

    const headers = {
      'Authorization': `Bearer ${config.HUBSPOT.ACCESS_TOKEN}`,
      'Content-Type': 'application/json'
    }

    const url = config.HUBSPOT.API
    const response = await axios.post(url, payload, { headers })
    if (response.status !== 200 && response.status !== 201) {
      return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: 'Error al integrar en Hubspot: ' + response.data.message }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: 'Integrado correctamente en Hubspot' }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: 'Error al integrar en Hubspot: ' + error.message }
  }
}

async function taxIdValidation ({supplier, isCreation, manualValidation}) {
  try {
    const isValidTaxId = validateTaxId(supplier.tax_id, supplier.country, manualValidation)
    if (!isValidTaxId) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: 'TAX ID inválido' }
    }

    if (!isCreation) {
      const payload = { ...supplier }

      const id = payload.id
      delete payload.id
      delete payload.supplier_steps
      delete payload.steps
      delete payload.__typename
      delete payload.general_status
      delete payload.general_status_message
      delete payload.last_step_message
      delete payload.last_step_status
      delete payload.users_associate

      await apolloClient.mutate({
        mutation: updateSupplier,
        variables: { id: Number(id), data: payload }
      })
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: 'TAX ID correcto' }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: 'Ha ocurrido un error al validar el TAX ID: ' + error.message }
  }
}

async function taxIdGobValidation ({supplier, isCreation}) {
  try {
    // const countriesToValidate = [REGISTER_BY_COUNTRY.ARGENTINA, REGISTER_BY_COUNTRY.PERU, REGISTER_BY_COUNTRY.CHILE]
    // if (!countriesToValidate.includes(supplier.country)) {
    //   return { status: VALIDATION_STATUS.MANUAL_ACTION, message: `El País seleccionado es ${supplier.country}, debe validarse manualmente.` }
    // }

    let queryToUse = null
    if (supplier.country === REGISTER_BY_COUNTRY.ARGENTINA) {
      queryToUse = getAfipSuppliersRegistered
    } else if (supplier.country === REGISTER_BY_COUNTRY.PERU) {
      queryToUse = getSunatSuppliersRegistered
    } else {
      queryToUse = getSiiSuppliersRegistered
    }

    const response = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: queryToUse,
      variables: { taxId: supplier.tax_id }
    })

    const data = response.data.hub_supplier_afip || response.data.hub_supplier_sunat || response.data.hub_supplier_sii

    const taxIdIsValidInGob = data.length > 0

    if (!taxIdIsValidInGob) {
      return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `TAX ID ${supplier.tax_id} NO encontrado en país indicado. Validar manualmente.` }
    }

    if (!isCreation) {
      const payload = { ...supplier }

      const id = payload.id
      delete payload.id
      delete payload.supplier_steps
      delete payload.steps
      delete payload.__typename
      delete payload.general_status
      delete payload.general_status_message
      delete payload.last_step_message
      delete payload.last_step_status
      delete payload.users_associate

      await apolloClient.mutate({
        mutation: updateSupplier,
        variables: { id: Number(id), data: payload }
      })
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} encontrado en país indicado.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: 'Ha ocurrido un error al validar el TAX ID: ' + error.message }
  }
}

function taxIdMarketplaceValidation ({supplier, marketplaceEnterprise}) {
  try {
    if (!marketplaceEnterprise) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} no existe en Marketplace UNILINK.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} encontrado en Marketplace UNILINK como "${marketplaceEnterprise.Name}".` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Ha ocurrido un error al validar el TAX ID ${supplier.tax_id} en Marketplace UNILINK: ${error.message}` }
  }
}

function taxIdLockedMarketplaceValidation ({supplier, marketplaceEnterprise}) {
  try {
    if (!marketplaceEnterprise) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} no existe en Marketplace UNILINK.` }
    }

    if (marketplaceEnterprise.IsBlocked) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR,
        message: `TAX ID ${supplier.tax_id}se cuentra BLOQUEADO en Marketplace UNILINK por ${marketplaceEnterprise.BlockReason || 'BLOQUEO NORMAL'}.`
      }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} se encuentra HABILITADO en Marketplace UNILINK.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Ha ocurrido un error al validar el TAX ID ${supplier.tax_id} en Marketplace UNILINK: ${error.message}` }
  }
}

function taxIdCat4mroValidation ({supplier, cat4mroOrganization}) {
  try {
    if (!cat4mroOrganization) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} NO encontrado en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} encontrado en ACCUSELLER como "${cat4mroOrganization.name}".` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Ha ocurrido un error al validar el TAX ID ${supplier.tax_id} en ACCUSELLER: ${error.message}` }
  }
}

function taxIdLockedCat4mroValidation ({supplier, cat4mroOrganization}) {
  try {
    if (!cat4mroOrganization) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} NO encontrado en ACCUSELLER.` }
    }

    if (cat4mroOrganization.IsLocked) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR,
        message: `TAX ID ${supplier.tax_id} se encuentra BLOQUEADO en ACCUSELLER por Falta de aceptación de T&C.`
      }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} se encuentra ACTIVO en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Ha ocurrido un error al validar el TAX ID ${supplier.tax_id} en ACCUSELLER: ${error.message}` }
  }
}

function taxIdTermsValidation ({supplier, organizationTermsAndConditions}) {
  try {
    if (!organizationTermsAndConditions) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} NO encontrado en ACCUSELLER.` }
    }

    if (organizationTermsAndConditions.length === 0) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `TAX ID ${supplier.tax_id} no ha firmado T&C en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `TAX ID ${supplier.tax_id} ha firmado T&C en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Ha ocurrido un error al validar el TAX ID ${supplier.tax_id} en ACCUSELLER: ${error.message}` }
  }
}

async function contactEmailFormatValidation ({supplier, isCreation}) {
  try {
    const isValidEmail = validateEmail(supplier.contact_email)

    if (!isValidEmail) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} es incorrecto.` }
    }

    if (!isCreation) {
      const payload = { ...supplier }

      const id = payload.id
      delete payload.id
      delete payload.supplier_steps
      delete payload.steps
      delete payload.__typename
      delete payload.general_status
      delete payload.general_status_message
      delete payload.last_step_message
      delete payload.last_step_status
      delete payload.users_associate

      await apolloClient.mutate({
        mutation: updateSupplier,
        variables: { id: Number(id), data: payload }
      })
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} ha sido validado correctamente.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} es inválido: ${error.message}` }
  }
}

function contactEmailSendgridValidation ({supplier}) {
  try {
    const isValidEmail = validateEmail(supplier.contact_email)

    if (!isValidEmail) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `El email ${supplier.contact_email} es incorrecto.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `El email ${supplier.contact_email} ha sido validado correctamente.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `El email ${supplier.contact_email} es inválido: ${error.message}` }
  }
}

function contactEmailCat4mroValidation ({supplier, cat4mroUser}) {
  try {
    if (!cat4mroUser || cat4mroUser.length === 0) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} se encuentra registrado en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} no existe.` }
  }
}

function contactEmailCat4mroDuplicateValidation ({supplier, cat4mroUser}) {
  try {
    if (!cat4mroUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en ACCUSELLER.` }
    }

    if (cat4mroUser.length > 1) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} se encuentra duplicado en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} sin duplicidad en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en ACCUSELLER.` }
  }
}

function contactEmailMarketplaceValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} se encuentra registrado en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en Marketplace.` }
  }
}

function contactEmailMarketplaceLockedValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en Marketplace.` }
    }

    if (!marketplaceUser.IsBlocked && marketplaceUser.IsActive) {
      return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} se encuentra HABILITADO en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} se encuentra BLOQUEADO en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en Marketplace.` }
  }
}

function contactEmailMarketplaceDuplicateValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO existe en Marketplace.` }
    }

    if (marketplaceUser.IsDuplicateMarketplace) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} se encuentra DUPLICADO en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} sin duplicidad en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en Marketplace.` }
  }
}

function contactEmailIdentityValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO existe en IDENTITY.` }
    }

    if (!marketplaceUser.IdentityId) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en IDENTITY.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} se encuentra registrado en IDENTITY.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en IDENTITY.` }
  }
}

function contactEmailIdentityDuplicateValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO existe en IDENTITY.` }
    }

    if (marketplaceUser.IsDuplicateIdentity) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} se encuentra DUPLICADO en IDENTITY.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} sin duplicidad en IDENTITY.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en IDENTITY.` }
  }
}

function contactEmailCredentialsValidation ({supplier, marketplaceUser}) {
  try {
    if (!marketplaceUser) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_email} NO encontrado en Marketplace.` }
    }

    if (!marketplaceUser.HaveActivityOnTheLastSixMonths) {
      return { status: VALIDATION_STATUS.PENDING, message: `Email ${supplier.contact_email} Sin actividad.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_email} Con actividad en los últimos 6 meses.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_email} NO existe en Marketplace.` }
  }
}

async function contactLegalEmailFormatValidation ({supplier, isCreation}) {
  try {
    const isValidEmail = validateEmail(supplier.contact_legal_email)

    if (!isValidEmail) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} es incorrecto.` }
    }

    if (!isCreation) {
      const payload = { ...supplier }

      const id = payload.id
      delete payload.id
      delete payload.supplier_steps
      delete payload.steps
      delete payload.__typename
      delete payload.general_status
      delete payload.general_status_message
      delete payload.last_step_message
      delete payload.last_step_status
      delete payload.users_associate

      await apolloClient.mutate({
        mutation: updateSupplier,
        variables: { id: Number(id), data: payload }
      })
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} ha sido validado correctamente.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} es inválido: ${error.message}` }
  }
}

function contactLegalEmailSendgridValidation ({supplier}) {
  try {
    const isValidEmail = validateEmail(supplier.contact_legal_email)

    if (!isValidEmail) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} es incorrecto.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} ha sido validado correctamente.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} es inválido: ${error.message}` }
  }
}

function contactLegalEmailCat4mroValidation ({supplier, cat4mroUserLegal}) {
  try {
    if (!cat4mroUserLegal || cat4mroUserLegal.length === 0) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} se encuentra registrado en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} no existe.` }
  }
}

function contactLegalEmailCat4mroDuplicateValidation ({supplier, cat4mroUserLegal}) {
  try {
    if (!cat4mroUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en ACCUSELLER.` }
    }

    if (cat4mroUserLegal.length > 1) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} se encuentra duplicado en ACCUSELLER.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} sin duplicidad en ACCUSELLER.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en ACCUSELLER.` }
  }
}

function contactLegalEmailMarketplaceValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} se encuentra registrado en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en Marketplace.` }
  }
}

function contactLegalEmailMarketplaceLockedValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en Marketplace.` }
    }

    if (!marketplaceUserLegal.IsBlocked && marketplaceUserLegal.IsActive) {
      return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} se encuentra HABILITADO en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} se encuentra BLOQUEADO en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en Marketplace.` }
  }
}

function contactLegalEmailMarketplaceDuplicateValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en Marketplace.` }
    }

    if (marketplaceUserLegal.IsDuplicateMarketplace) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} se encuentra DUPLICADO en Marketplace.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} sin duplicidad en Marketplace.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en Marketplace.` }
  }
}

function contactLegalEmailIdentityValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en IDENTITY.` }
    }

    if (!marketplaceUserLegal.IdentityId) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en IDENTITY.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} se encuentra registrado en IDENTITY.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en IDENTITY.` }
  }
}

function contactLegalEmailIdentityDuplicateValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en IDENTITY.` }
    }

    if (marketplaceUserLegal.IsDuplicateIdentity) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} se encuentra DUPLICADO en IDENTITY.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} sin duplicidad en IDENTITY.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en IDENTITY.` }
  }
}

function contactLegalEmailCredentialValidation ({supplier, marketplaceUserLegal}) {
  try {
    if (!marketplaceUserLegal) {
      return { status: VALIDATION_STATUS.VALIDATION_ERROR, message: `Email ${supplier.contact_legal_email} NO encontrado en Marketplace.` }
    }

    if (!marketplaceUserLegal.HaveActivityOnTheLastSixMonths) {
      return { status: VALIDATION_STATUS.PENDING, message: `Email ${supplier.contact_legal_email} Sin actividad.` }
    }

    return { status: VALIDATION_STATUS.SUCCESS, message: `Email ${supplier.contact_legal_email} Con actividad en los últimos 6 meses.` }
  } catch (error) {
    return { status: VALIDATION_STATUS.SYSTEM_ERROR, message: `Email ${supplier.contact_legal_email} NO existe en Marketplace.` }
  }
}

const stepsMap = {
  hubspotIntegration,
  taxIdValidation,
  contactEmailFormatValidation,
  contactLegalEmailFormatValidation,
  taxIdGobValidation,
  taxIdMarketplaceValidation,
  taxIdCat4mroValidation,
  taxIdTermsValidation,
  taxIdLockedMarketplaceValidation,
  taxIdLockedCat4mroValidation,
  contactEmailSendgridValidation,
  contactEmailCat4mroValidation,
  contactEmailCat4mroDuplicateValidation,
  contactEmailIdentityValidation,
  contactEmailIdentityDuplicateValidation,
  contactEmailMarketplaceValidation,
  contactEmailMarketplaceLockedValidation,
  contactEmailMarketplaceDuplicateValidation,
  contactEmailCredentialsValidation,
  contactLegalEmailSendgridValidation,
  contactLegalEmailCat4mroValidation,
  contactLegalEmailCat4mroDuplicateValidation,
  contactLegalEmailIdentityValidation,
  contactLegalEmailIdentityDuplicateValidation,
  contactLegalEmailMarketplaceValidation,
  contactLegalEmailMarketplaceLockedValidation,
  contactLegalEmailMarketplaceDuplicateValidation,
  contactLegalEmailCredentialValidation
}

const hubSuppliers = {
  namespaced: true,
  actions: {
    async executeValidations ({ commit }, { supplier, steps, manualValidation = false, allExistingSteps = [], hasTraceability = false }) {
      let marketplaceEnterprise = null
      let marketplaceUser = null
      let marketplaceUserLegal = null
      let cat4mroOrganization = null
      let cat4mroUser = null
      let cat4mroUserLegal = null
      let organizationTermsAndConditions = null

      try {
        if (steps.length < allExistingSteps.length) {
          const missingSteps = allExistingSteps.filter(step => steps.length === 0
            ? step.is_creation_step
            : !steps.some(s => s.step_type === step.name)
          )

          missingSteps.forEach(step => {
            const newStep = {
              step_type: step.name,
              status: VALIDATION_STATUS.PENDING,
              message: null,
              validated_at: null,
              priority: 0
            }

            if (supplier.id) {
              newStep.register_id = supplier.id
            }

            if (step.allow_send_credentials) {
              insertStep(newStep)
              return
            }

            steps.push(newStep)
          })
        }

        const filteredSteps = steps.filter(step => RETRY_STATUS.includes(step.status) && !SEND_CREDENTIALS_STEPS.includes(step.step_type))

        if (filteredSteps.some(step => DETERMINISTIC_MARKETPLACE_STEPS.includes(step.step_type))) {
          marketplaceEnterprise = await getMarketplaceEnterprise(supplier.tax_id)
        }
        if (filteredSteps.some(step => DETERMINISTIC_CAT4MRO_STEPS.includes(step.step_type))) {
          cat4mroOrganization = await getCat4mroOrganization(supplier.tax_id)
          if (cat4mroOrganization) {
            organizationTermsAndConditions = await getOrganizationTermsAndConditions(cat4mroOrganization.id)
          }
        }
        if (filteredSteps.some(step => step.step_type.includes(DETERMINISTIC_STEPS.contact_email) &&
                                       step.step_type !== DETERMINISTIC_STEPS.contact_email_format_validation)) {
          marketplaceUser = await getMarketplaceUser(supplier.contact_email)
          cat4mroUser = await getCat4mroUser(supplier.contact_email)
        }
        if (filteredSteps.some(step => step.step_type.includes(DETERMINISTIC_STEPS.contact_legal_email) &&
                                       step.step_type !== DETERMINISTIC_STEPS.contact_legal_email_format_validation)) {
          marketplaceUserLegal = await getMarketplaceUser(supplier.contact_legal_email)
          cat4mroUserLegal = await getCat4mroUser(supplier.contact_legal_email)
        }

        await Promise.all(filteredSteps.map(async step => {
          const normalizedStepType = step.step_type.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          )
          const stepFunction = stepsMap[normalizedStepType]

          if (!stepFunction || typeof stepFunction !== 'function') {
            console.error(`Step function not found for step type: ${step.step_type}`)
            return
          }

          const isCreation = !step.id
          const params = {
            supplier,
            isCreation,
            marketplaceEnterprise,
            marketplaceUser,
            marketplaceUserLegal,
            cat4mroOrganization,
            cat4mroUser,
            cat4mroUserLegal,
            organizationTermsAndConditions
          }

          if (MANUAL_VALIDATION_STEPS.includes(step.step_type)) {
            params.manualValidation = manualValidation
          }
          
          const result = await stepFunction(params)

          if (supplier.id && hasTraceability) {
            if (isCreation) {
              delete step.__typename
              step.message = result.message
              step.status = result.status
              step.validated_at = new Date().toISOString()
              await insertStep(step)
            } else {
              await updateStep(step, result.status, result.message)
            }
          } else {
            steps.find(s => s.step_type === step.step_type).status = result.status
            steps.find(s => s.step_type === step.step_type).message = result.message
            steps.find(s => s.step_type === step.step_type).validated_at = new Date().toISOString()
          }
        }))
        return steps
      } catch (error) {
        console.error('Error executing validations:', error)
        throw error
      }
    }
  }
}

export default hubSuppliers



// WEBPACK FOOTER //
// ./src/store/modules/hubSuppliersValidators.js